import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import ImageCropper from './ImageCropper';
import { api } from '../../../services/api';
import ImageIcon from '@mui/icons-material/Image';
import { styled } from '@mui/system';
import browserImageCompression from 'browser-image-compression';

const Input = styled('input')({
  display: 'none',
});

interface ProfilePictureDialogProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: (newUrl: string) => void;
  currentPicture?: string;
}

const ProfilePictureDialog: FC<ProfilePictureDialogProps> = ({
  open,
  onClose,
  onUploadSuccess,
  currentPicture,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImageFile, setCroppedImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (reader.result) {
          setSelectedImage(reader.result as string);
        }
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCancelCrop = () => {
    setSelectedImage(null);
    setCroppedImageFile(null);
    setUploadError(null);
  };

  const handleCropComplete = (croppedFile: File) => {
    setCroppedImageFile(croppedFile);
    handleUpload(croppedFile);
  };

  const handleUpload = async (croppedFile: File) => {
    try {
      setUploading(true);
      setUploadError(null);
  
      // Compressão da imagem antes do upload
      const compressedFile = await browserImageCompression(croppedFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 500,
        useWebWorker: true,
      });
  
      // Cria o FormData para envio
      const formData = new FormData();
      formData.append('profile_picture', compressedFile, 'profile_picture.jpg');
  
      // Envie a imagem para o backend
      const response = await api.post('users/me/profile-picture', formData);
  
      // Supondo que o backend retorne a URL da imagem
      const { url } = response.data;
      onUploadSuccess(url);
  
      // Resetar estados
      setSelectedImage(null);
      setCroppedImageFile(null);
      onClose();
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem:', error);
      setUploadError('Falha ao fazer upload da imagem. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };  

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Atualizar Foto de Perfil</DialogTitle>
      <DialogContent>
        {!selectedImage ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            <label htmlFor="upload-button">
              <Input accept="image/*" id="upload-button" type="file" onChange={handleFileChange} />
              <Button variant="contained" component="span" startIcon={<ImageIcon />}>
                Selecionar Imagem
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Escolha uma imagem para atualizar sua foto de perfil.
            </Typography>
            {uploadError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        ) : (
          <ImageCropper
            imageSrc={selectedImage}
            onCropComplete={handleCropComplete}
            onCancel={handleCancelCrop}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!selectedImage && (
          <Button onClick={onClose} disabled={uploading}>
            Cancelar
          </Button>
        )}
        {selectedImage && (
          <Button onClick={() => setSelectedImage(null)} disabled={uploading}>
            Cancelar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePictureDialog;